import { cssThemedValue } from '@creditornot/cb-ingredients';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { WoltModal } from 'common/components';
import { useShareMenuWithVenuesTracking } from 'modules/menu/hooks/telemetryHooks';
import { useLiveSelectionData } from 'modules/shared-menu/hooks/useLiveSelectionData';

import { ConfirmAndShare } from './ConfirmAndShare';
import { SelectVenues } from './SelectVenues';
import { linkVenuesToSharedMenu } from '../../../../api';
import { useScopeContext } from '../../../../contexts/ScopeContext';
import { useSharedMenuData } from '../../../../hooks/useSharedMenuData';
import { useVenueList } from '../../../../hooks/useVenueList';
import { VenueWithSharedMenuLinkInfo } from '../../../../hooks/useVenuesWithSelectionLinks';
import { SharedMenu } from '../../../../types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  sharedMenu: SharedMenu;
  venues: VenueWithSharedMenuLinkInfo[];
};

const Root = styled(WoltModal)`
  width: 760px;
  height: 80vh;
  max-height: 700px;
  border-radius: 8px;
  background-color: ${cssThemedValue('surfaceMain')};
  display: flex;
  flex-direction: column;
`;

export const ShareWithVenuesModal = ({ isOpen, onClose, sharedMenu, venues }: Props) => {
  const scope = useScopeContext();

  const [modalStep, setModalStep] = useState<'select-venues' | 'confirm'>('select-venues');

  const { refetchSharedMenus } = useSharedMenuData({ scope });
  const { selectionData, selectionDataLoading } = useLiveSelectionData({ selectionId: sharedMenu.id });

  const venuesNotLinkedToCurrentSharedMenu = useMemo(
    () =>
      venues.filter(
        venue =>
          venue.sharedMenuLink.scope !== sharedMenu.scope.type || venue.sharedMenuLink.sharedMenuId !== sharedMenu.id,
      ) ?? [],
    [sharedMenu, venues],
  );

  const {
    searchInput,
    searchVenues,
    selectedVenueIds,
    onRowClick,
    shownVenues,
    selectAllShownVenues,
    deselectAllShownVenues,
    selectedVenues,
    selectedShownVenues,
  } = useVenueList({
    venues: venuesNotLinkedToCurrentSharedMenu,
  });

  const { mutate, isLoading, error, reset } = useMutation(
    () => {
      if (selectedVenueIds.size === 0) {
        return Promise.reject();
      }

      return linkVenuesToSharedMenu(sharedMenu.id, [...selectedVenueIds]);
    },
    {
      onSuccess: async () => {
        await refetchSharedMenus();
        toast.success(<FormattedMessage id="shared-menu.link.success" />);
        handleClose();
      },
    },
  );

  const handleVenueRowClick = useCallback(
    (venueId: string) => {
      onRowClick(venueId);
    },
    [onRowClick],
  );

  const isMenuEmpty = useMemo(() => selectionData?.offers.length === 0, [selectionData]);
  const selectedVenueIdsAsArray = useMemo(() => [...selectedVenueIds], [selectedVenueIds]);

  const trackShareWithVenues = useShareMenuWithVenuesTracking(
    selectedVenueIds.size === venues.length,
    sharedMenu.id,
    searchInput,
    selectedVenueIdsAsArray,
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
    deselectAllShownVenues();
    searchVenues('');
    setModalStep('select-venues');
  }, [reset, onClose, deselectAllShownVenues, searchVenues]);

  return (
    <Root isOpen={isOpen} onRequestClose={handleClose}>
      {modalStep === 'select-venues' && (
        <SelectVenues
          onClose={handleClose}
          onContinue={() => setModalStep('confirm')}
          onDeselectAllShownVenues={deselectAllShownVenues}
          onSearchVenues={searchVenues}
          onSelectAllShownVenues={selectAllShownVenues}
          onVenueRowClick={handleVenueRowClick}
          searchInput={searchInput}
          selectedVenueIds={selectedVenueIds}
          selectedVenues={selectedVenues}
          sharedMenu={sharedMenu}
          shownVenues={shownVenues ?? []}
          selectedShownVenues={selectedShownVenues}
          selectionDataLoading={selectionDataLoading}
        />
      )}

      {modalStep === 'confirm' && (
        <ConfirmAndShare
          error={error}
          submitting={isLoading}
          onGoBack={() => setModalStep('select-venues')}
          onClose={handleClose}
          onSubmit={() => {
            trackShareWithVenues();
            mutate();
          }}
          selectedVenues={selectedVenues}
          sharedMenu={sharedMenu}
          isMenuEmpty={isMenuEmpty}
        />
      )}
    </Root>
  );
};
